import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogActions,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from "@mui/material";
import api from "../../../services/api";
import CostProfileTable from "./CostProfileTable";
import CostProfileCardList from "./CostProfileCardList";
import CostProfileFormModal from "./CostProfileForm/CostProfileFormModal";
import ReusableComponentFormModal from "./ReusableComponentForm/ReusableComponentFormModal";
import { useSnackbarAlert } from "../../../hooks/useSnackbarAlert";

const AdminCostProfiles = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [showComponentForm, setShowComponentForm] = useState(false);
  const [tab, setTab] = useState("products"); // "products" | "components"

  const snackbar = useSnackbarAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/cost-profiles");
      setProfiles(res.data);
    } catch (err) {
      snackbar.error("Failed to load cost profiles.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
  }, []);

  const handleDeleteProfile = async () => {
    if (!pendingDelete) return;
    try {
      await api.delete(`/admin/cost-profiles/${pendingDelete._id}`);
      snackbar.success("Profile deleted.");
      setPendingDelete(null);
      fetchProfiles();
    } catch (err) {
      snackbar.error("Failed to delete profile.");
    }
  };

  const handleSyncProfile = async (profile) => {
    try {
      await api.patch(`/admin/cost-profiles/${profile._id}/sync`);
      snackbar.success("Snapshot refreshed.");
      fetchProfiles();
    } catch {
      snackbar.error("Failed to refresh cost snapshot.");
    }
  };

  const scope = tab === "components" ? "components" : "products";

  return (
    <Box p={{ xs: 2, md: 3 }}>
      <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
        Product Cost Profiles
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
        textColor="inherit">
        <Tab value="products" label="Products" />
        <Tab value="components" label="Reusable Components" />
      </Tabs>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 3 }}>
        {scope === "products" ? (
          <Button
            fullWidth={isMobile}
            variant="contained"
            onClick={() => setEditing({})}>
            + Add Product Profile
          </Button>
        ) : (
          <Button
            fullWidth={isMobile}
            variant="contained"
            onClick={() => setShowComponentForm(true)}>
            + Add Reusable Component
          </Button>
        )}
      </Stack>

      {isMobile ? (
        <CostProfileCardList
          profiles={profiles}
          onEdit={(p) => setEditing(p)}
          onDelete={(p) => setPendingDelete(p)}
          onSync={handleSyncProfile}
        />
      ) : (
        <CostProfileTable
          profiles={profiles}
          onEdit={(p) => setEditing(p)}
          onDelete={(p) => setPendingDelete(p)}
          onSync={handleSyncProfile}
          scope={scope} // <-- this hides the pill & section header on single tabs
        />
      )}

      <CostProfileFormModal
        open={!!editing}
        initialData={editing}
        onClose={() => setEditing(null)}
        onSuccess={fetchProfiles}
      />

      <ReusableComponentFormModal
        open={showComponentForm}
        onClose={() => setShowComponentForm(false)}
        onSuccess={fetchProfiles}
      />

      <Dialog
        fullWidth
        maxWidth="xs"
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPendingDelete(null)}>Cancel</Button>
          <Button
            onClick={handleDeleteProfile}
            variant="contained"
            color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCostProfiles;
